import {
  failureCode,
  type PaymentPlatform,
  type ServiceLevelStatus,
} from '@forest/paywall-core'
import {
  selectCurrentPlan,
  selectExpiringSubscription,
  type CurrentPlan,
  type ExpiringSubscription,
} from '~/lib/expiring-subscription'
import type { Loaded } from '~/lib/loaded'
import { readPlatform } from '~/lib/params'
import {
  clusterProductIds,
  loadStoreProducts,
  platformPlans,
  resolvePlans,
  type Plans,
} from '~/lib/plans'
import type { Screen } from '~/lib/screen'
import { loadServiceLevel, subscriptionRecord } from '~/lib/service-level'
import { loadWinBackArm } from '~/lib/win-back-experiment'
import { loadUserInfo } from '~/services/api'

/**
 * What the launch screen admits a user with (GLOSSARY.md "Resubscribe
 * paywall").
 */
interface Admission {
  /**
   * Resubscribe confirmation's baseline (GLOSSARY.md): the expiry of the
   * pre-purchase subscription record — an expired one that has not cleared
   * yet included — or `null` with no record. An unparseable expiry stays
   * NaN, which no refresh of that same record can move off.
   */
  confirmationBaseline: number | null
  /**
   * Whether the buyer is an expiring subscriber (GLOSSARY.md) rather than a
   * lapsed one, which decides what a purchase ends on: renewal resumed or
   * Plus regained (GLOSSARY.md). Read once here, so it holds even once the
   * expiry passes while the page stays open.
   */
  isExpiringSubscriber: boolean
  /**
   * Only when launched at the loss screen, the one screen that renders it.
   * Launched at the plan picker there is no loss screen to return to.
   */
  subscription: ExpiringSubscription | null
}

export interface Paywall extends Admission {
  plans: Plans
  /**
   * An expiring subscriber's current plan (GLOSSARY.md), whichever screen the
   * page launched at; read once here, so crossing the expiry while the page
   * stays open does not change it.
   */
  currentPlan: CurrentPlan | null
  /** The platform the plans resolved on, which the purchase runs on. */
  platform: PaymentPlatform
}

/** Why the page cannot show (GLOSSARY.md "Resubscribe paywall"). */
export interface Unavailable {
  /** The failure code the error dialog shows (ADR 0037). */
  code: string
  /** What was thrown, for a `load:unexpected` failure. */
  message?: string
}

/**
 * Who the page applies to depends on the launch screen (GLOSSARY.md
 * "Resubscribe paywall"). The loss screen needs an expiring subscription.
 * The plan picker shows to anyone whose subscription is not auto-renewing,
 * a lapsed subscriber included; a record with auto-renew on, expired or not,
 * is a store still retrying the renewal or a subscriber already renewing,
 * and buying would charge them twice. Unavailable means the page does not
 * apply.
 */
function admitAt(
  launchScreen: Screen,
  status: Loaded<ServiceLevelStatus>,
  nowMs: number,
): Admission | Unavailable {
  if (!status.ok) {
    return { code: failureCode('load_service_level', status.cause) }
  }
  const expiring = selectExpiringSubscription(status.value, nowMs)
  if (launchScreen === 'loss') {
    if (!expiring) {
      return { code: failureCode('load_admission', 'not_expiring') }
    }
    return {
      subscription: expiring,
      confirmationBaseline: expiring.expiresAtMs,
      isExpiringSubscriber: true,
    }
  }
  const record = subscriptionRecord(status.value)
  if (record?.is_auto_renewing) {
    return { code: failureCode('load_admission', 'auto_renewing') }
  }
  return {
    subscription: null,
    confirmationBaseline: record && Date.parse(record.expires_at),
    isExpiringSubscriber: expiring !== null,
  }
}

async function assemblePaywall(
  launchScreen: Screen,
): Promise<Paywall | Unavailable> {
  const platform = readPlatform()
  const platformRow = platformPlans(platform)
  const [status, arm, storeProducts, userInfo] = await Promise.all([
    loadServiceLevel(),
    loadWinBackArm(),
    // An unsupported platform has no plans to look up.
    platformRow ? loadStoreProducts() : null,
    loadUserInfo(),
  ])
  // The first failure wins, admission first: a user the page does not apply
  // to is turned away whatever else failed to load.
  const nowMs = Date.now()
  const admission = admitAt(launchScreen, status, nowMs)
  if ('code' in admission) return admission
  if (!platform || !platformRow) {
    return { code: failureCode('load_admission', 'unsupported_platform') }
  }
  if (!userInfo.ok) {
    return { code: failureCode('load_user_info', userInfo.cause) }
  }
  const productIds = clusterProductIds(userInfo.value)
  if (!productIds) {
    return { code: failureCode('load_user_info', 'unknown_cluster') }
  }
  // Null only without a platform row, which is turned away above.
  if (!storeProducts?.ok) {
    return {
      code: failureCode(
        'load_store_products',
        storeProducts?.cause ?? 'unexpected',
      ),
    }
  }
  const plans = resolvePlans(platformRow, productIds, storeProducts.value, arm)
  if (!plans) {
    return { code: failureCode('load_store_products', 'plan_missing') }
  }
  const currentPlan = status.ok
    ? selectCurrentPlan(status.value, storeProducts.value, nowMs)
    : null
  return { ...admission, plans, platform, currentPlan }
}

/**
 * Everything the page needs before it is revealed — service level,
 * experiment group, products and the user's cluster — loaded in parallel —
 * or why the page cannot show at the screen it was launched at.
 */
export async function loadPaywall(
  launchScreen: Screen,
): Promise<Paywall | Unavailable> {
  try {
    return await assemblePaywall(launchScreen)
  } catch (error) {
    // Each load already turns its own failure into a code, so a throw is
    // native data the page could not read.
    return { code: failureCode('load', 'unexpected'), message: String(error) }
  }
}
